<template>
  <div>
    <DataTableWrapper
      ref="tableWrapper"
      :initialSortBy="[{ field: 'createdAt', dir: 'desc' }]"
      :data-source="{
        type: 'AxisGQL',
        query: require('@/services/gql/types/Kiosk/queries/kiosksGrid.gql')
      }"
      :headers="$options.headersConfig"
      :filters="{
        kioskName: { control: 'text', modifier: 'contains' },
        isActive: {
          control: 'select',
          options: [
            { value: true, text: 'Да' },
            { value: false, text: 'Нет' }
          ],
          dataType: Boolean
        },
        isOnline: {
          control: 'select',
          options: [
            { value: true, text: 'Да' },
            { value: false, text: 'Нет' }
          ],
          dataType: Boolean
        },
        serialNumber: { control: 'text', modifier: 'contains' },
        type: {
          control: 'select',
          options: $options.kioskTypes
        },
        'place.title': { control: 'text', modifier: 'contains' },
        'city.title': { control: 'text', modifier: 'contains' }
      }"
      @query-state-updated="getStateQuery"
      :stored-headers="getCurrentViewCols"
      :views-list="getViewList"
      :active-view-key="getActiveViewKey"
      :views-loading="$async.updateMe.$pending"
      @view-changed="updateStoredView"
      @active-view-changed="updateStoredActiveView"
      @remove-view="removeStoredView"
      @data-loaded="onTableDataChange"
    >
      <template v-slot="{ tableProps, tableListeners, filtersProps, filtersListeners }">
        <v-data-table
          fixed-header
          height="80vh"
          :items-per-page="$options.perPage.itemsPerPage"
          :footer-props="{ itemsPerPageOptions: $options.perPage.itemsPerPageOptions }"
          v-bind="tableProps"
          v-on="tableListeners"
          v-model="mixSelectedItems"
        >
          <template v-for="(h, i) in $options.headersConfig" v-slot:[`header.${h.value}`]>
            <v-tooltip open-delay="350" :key="i" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ h.text }}</span>
              </template>
              <span>{{ h.description }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`body.prepend`]="{}">
            <DataTableFilters
              is-grid-selectable
              v-bind="filtersProps"
              v-on="filtersListeners"
            />
          </template>
          <template
            v-for="(h, i) in $options.headersConfig"
            v-slot:[`item.${h.value}`]="{ item }"
          >
            <div :key="i">
              <div v-if="h.type === 'number' && item[h.value] !== null">
                {{ formatAmount(item[h.value], '0') }}
              </div>
              <div v-else-if="h.text === 'Имя'">
                <router-link
                  title="Редактировать"
                  :to="{
                    name: 'page-kiosk-device',
                    params: { id: item.id.toString() }
                  }"
                  >
                  {{ item[h.value] }}
                </router-link>
              </div>
              <div v-else-if="h.text === 'Тип'">
               {{ $options.kioskTypes.find(type => type.value === item[h.value])?.text }}
              </div>
              <div v-else-if="h.text === 'Город'">
                <router-link
                  title="Открыть"
                  :to="{
                    name: 'page-city',
                    params: { id: item?.city?.id }
                  }"
                >
                  {{ item?.city?.title }}
                </router-link>
              </div>
              <div v-else-if="h.type === 'date'">
                <DateTimeCell v-if="item[h.value]" :value="item[h.value]" />
                <div v-else>-</div>
              </div>
              <div v-else-if="h.type === 'boolean'">
                <v-icon
                  v-text="'mdi-checkbox-marked-circle'"
                  :color="item[h.value] ? 'success' : 'grey'"
                />
              </div>
              <div v-else-if="h.type === 'actions'">
                <router-link
                  :to="{
                    name: 'page-kiosk-device',
                    params: { id: item.id.toString() }
                  }"
                  title="Редактировать"
                  ><v-btn icon><v-icon>mdi-pencil</v-icon></v-btn></router-link
                >
                <a title="Удалить">
                  <v-btn icon @click="deleteKiosk(item.id)">
                    <v-icon color="red">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </a>
              </div>

              <div v-else>
                {{ get(item, h.value) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </template>
    </DataTableWrapper>
    <ConfirmationDialog ref="confirmDeleteKiosk" />
  </div>
</template>

<script>
import kioskTypes from '@/configs/kioskTypes'
import DataTableWrapper from '@/components/data-table-wrapper'
import DataTableFilters from '@/components/data-table-filters'
import ConfirmationDialog from '@/components/confirmation-dialog'
import { formatAmount, get } from '@/utils'
import { paramsToListQueryInput } from '@/components/data-table-wrapper/data-sources/axis-gql/utils'
import perPage from '@/configs/v-data-table.js'
import headersConfig from './headersConfig'
import deleteKioskMutation from '@/services/gql/types/Kiosk/mutations/deleteKiosk.gql'
import { gridDependentMixin } from '@/pages/grid-dependent.mix'
import updateMeMutation from '@/services/gql/types/Me/mutations/updateMe.gql'
import DateTimeCell from '@/components/date-time-cell'

export default {
  perPage,
  headersConfig,
  kioskTypes,
  name: 'GridKioskDevices',
  gridType: 'kioskDevices',
  mixins: [gridDependentMixin],
  components: {
    DataTableWrapper,
    DataTableFilters,
    ConfirmationDialog,
    DateTimeCell
  },
  props: {
    items: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      queryState: null
    }
  },
  methods: {
    get,
    refetch () {
      this.$refs.tableWrapper.refresh()
    },
    formatAmount,
    getStateQuery (state) {
      this.queryState = paramsToListQueryInput(state)
    },
    async deleteKiosk (id) {
      try {
        await this.$refs.confirmDeleteKiosk.open()
        await this.$async.deleteKiosk.$perform({ id })
        this.$successMsg('Киоск удален')
        this.refetch()
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении киоска ${id}`)
      }
    }
  },
  async: {
    deleteKiosk (variables) {
      return this.$apollo.mutate({
        mutation: deleteKioskMutation,
        variables
      })
    },
    updateMe (variables) {
      return this.$apollo.mutate({
        mutation: updateMeMutation,
        variables
      })
    }
  }
}
</script>
